import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'
import type { ApiResponse } from '@/types'

export interface PolicyFeature {
  id: number
  feature: string
  serviceId?: number | null
  serviceName?: string | null
  featureUniqueId?: string | null
  sidebarKey?: string | null
  parentId?: number | null
  parentFeature?: string | null
  sortOrder?: number | null
  childrenCount?: number
  serviceVariants?: Array<{
    id: number
    variantUniqueId?: string | number | null
    name?: string | null
  }>
  branchHidden?: boolean
  disabledByParent?: boolean
  selected?: boolean
}

export interface PolicyFeatureListResponse {
  features: PolicyFeature[]
  serviceId?: number | null
  serviceInstanceId?: string | null
  serviceVariantId?: number | null
  serviceVariantName?: string | null
}

type PolicyFeatureRecord = {
  id: number
  feature: string
  service_id?: number | null
  service_name?: string | null
  feature_unique_id?: string | null
  sidebar_key?: string | null
  parent_id?: number | null
  parent_feature?: string | null
  sort_order?: number | null
  children_count?: number
  service_variants?: Array<{
    id: number
    variant_unique_id?: string | number | null
    name?: string | null
  }>
  branch_hidden?: boolean
  disabled_by_parent?: boolean
  selected?: boolean
}

type RawFeaturePayload = {
  features?: PolicyFeatureRecord[]
  service_id?: number | null
  service_instance_id?: string | null
  service_variant_id?: number | null
  service_variant_name?: string | null
}

const extractPayload = <T>(payload: ApiResponse<T> | T): T => (
  typeof payload === 'object' && payload !== null && 'data' in payload
    ? payload.data
    : payload
) as T

const normalizeFeature = (feature: PolicyFeatureRecord): PolicyFeature => ({
  id: feature.id,
  feature: feature.feature,
  serviceId: feature.service_id,
  serviceName: feature.service_name,
  featureUniqueId: feature.feature_unique_id,
  sidebarKey: feature.sidebar_key,
  parentId: feature.parent_id,
  parentFeature: feature.parent_feature,
  sortOrder: feature.sort_order,
  childrenCount: feature.children_count ?? 0,
  serviceVariants: Array.isArray(feature.service_variants)
    ? feature.service_variants.map((variant) => ({
        id: variant.id,
        variantUniqueId: variant.variant_unique_id,
        name: variant.name,
      }))
    : [],
  branchHidden: Boolean(feature.branch_hidden),
  disabledByParent: Boolean(feature.disabled_by_parent),
  selected: feature.selected,
})

export const policyFeaturesApi = {
  listCurrentService: async ({
    roleId,
    serviceId,
    serviceVariant,
    serviceInstanceId,
    includeHiddenFeatures,
  }: {
    roleId: string | number
    serviceId?: string | number | null
    serviceVariant?: string | number | null
    serviceInstanceId?: string | null
    includeHiddenFeatures?: boolean
  }): Promise<PolicyFeatureListResponse> => {
    const response = await api.get<ApiResponse<RawFeaturePayload> | RawFeaturePayload>(API_PATHS.policy.features.details, {
      params: {
        role_id: roleId,
        service_id: serviceId || undefined,
        service_variant: serviceVariant || undefined,
        service_instance_id: serviceInstanceId || undefined,
        include_hidden_features: includeHiddenFeatures ? 1 : undefined,
      },
    })

    const payload = extractPayload(response.data)

    return {
      features: Array.isArray(payload.features) ? payload.features.map(normalizeFeature) : [],
      serviceId: payload.service_id,
      serviceInstanceId: payload.service_instance_id,
      serviceVariantId: payload.service_variant_id,
      serviceVariantName: payload.service_variant_name,
    }
  },

  create: async ({
    serviceId,
    feature,
    sidebarKey,
    parentId,
    sortOrder,
    serviceVariantIds,
  }: {
    serviceId: number | string
    feature: string
    sidebarKey?: string | null
    parentId?: number | null
    sortOrder?: number | null
    serviceVariantIds?: Array<string | number>
  }) => {
    const response = await api.post(API_PATHS.policy.features.create, {
      service_id: serviceId,
      feature,
      sidebar_key: sidebarKey || undefined,
      parent: parentId || null,
      sort_order: sortOrder ?? undefined,
      service_variant_ids: serviceVariantIds,
    })

    return extractPayload(response.data)
  },

  update: async ({
    id,
    feature,
    sidebarKey,
    parentId,
    sortOrder,
    serviceVariantIds,
    serviceInstanceId,
    branchHidden,
  }: {
    id: number | string
    feature?: string
    sidebarKey?: string | null
    parentId?: number | null
    sortOrder?: number | null
    serviceVariantIds?: Array<string | number>
    serviceInstanceId?: string | null
    branchHidden?: boolean
  }) => {
    const response = await api.patch(API_PATHS.policy.features.update(id), {
      feature,
      sidebar_key: sidebarKey === '' ? null : sidebarKey,
      parent: parentId === undefined ? undefined : parentId || null,
      sort_order: sortOrder ?? undefined,
      service_variant_ids: serviceVariantIds,
      service_instance_id: serviceInstanceId || undefined,
      branch_hidden: typeof branchHidden === 'boolean' ? branchHidden : undefined,
    })

    return extractPayload(response.data)
  },

  remove: async (id: number | string) => {
    const response = await api.delete(API_PATHS.policy.features.delete(id))
    return extractPayload(response.data)
  },
}